import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../../_core/_services/notification.service';
import { ComplianceAdminService } from '../../../_core/_services/_compliance/compliance-admin.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';

@Component({
  selector: 'app-admin-policy-editor',
  standalone: false,
  templateUrl: './admin-policy-editor.html',
  styleUrl: './admin-policy-editor.css',
})
export class AdminPolicyEditor implements OnInit {
  form!: FormGroup;
  loading = false;
  saving = false;
  lookupsLoading = false;
  error: string | null = null;

  id = '';
  isEditMode = false;

  jurisdictions: any[] = [];
  tiers: any[] = [];

  policyStatuses = [
    { label: 'Draft', value: 1 },
    { label: 'Active', value: 2 },
    { label: 'Retired', value: 3 }
  ];

  customerTypes = [
    { label: 'Individual', value: 1 },
    { label: 'Business', value: 2 }
  ];

  productTypes = [
    { label: 'Wallet', value: 1 },
    { label: 'Trading', value: 2 },
    { label: 'Remittance', value: 3 }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: ComplianceAdminService,
    private notifyService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') || '';
    this.isEditMode = !!this.id;

    this.form = this.fb.group({
      jurisdictionId: ['', [Validators.required]],
      customerType: [{ value: 1, disabled: true }, [Validators.required]],
      productType: [1, [Validators.required]],
      tierId: ['', [Validators.required]],
      tierCode: ['', [Validators.required, Validators.maxLength(30)]],
      version: [1, [Validators.required, Validators.min(1)]],
      name: ['', [Validators.required, Validators.maxLength(150)]],
      status: [1, [Validators.required]],
      requiresManualReview: [false],
      requiresSanctionsScreening: [false],
      requiresSourceOfFunds: [false],
      requiresProofOfAddress: [false],
      effectiveFrom: ['', [Validators.required]],
      effectiveTo: ['']
    });

    this.loadLookups();
  }

  loadLookups(): void {
    this.lookupsLoading = true;
    this.error = null;

    forkJoin({
      jurisdictions: this.adminService.getJurisdictions({ page: 1, pageSize: 500 }),
      tiers: this.adminService.getTiers({ page: 1, pageSize: 500 })
    })
      .pipe(finalize(() => (this.lookupsLoading = false)))
      .subscribe({
        next: (res: any) => {
          this.jurisdictions = res?.jurisdictions?.data || res?.jurisdictions || [];
          this.tiers = res?.tiers?.data || res?.tiers || [];

          if (this.isEditMode) {
            this.load();
          }
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load lookup data.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  load(): void {
    if (!this.id) return;

    this.loading = true;
    this.error = null;

    this.adminService.getPolicyById(this.id)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          const item = res?.data || res;

          if (!item) {
            this.error = 'Policy not found.';
            return;
          }

          this.form.patchValue({
            jurisdictionId: item.jurisdictionId || '',
            customerType: this.getCustomerTypeValue(item.customerType ?? 1),
            productType: item.productType ?? 1,
            tierId: this.findTierIdForPolicy(item),
            tierCode: item.tierCode || '',
            version: item.version ?? 1,
            name: item.name || '',
            status: item.status ?? 1,
            requiresManualReview: item.requiresManualReview ?? false,
            requiresSanctionsScreening: item.requiresSanctionsScreening ?? false,
            requiresSourceOfFunds: item.requiresSourceOfFunds ?? false,
            requiresProofOfAddress: item.requiresProofOfAddress ?? false,
            effectiveFrom: item.effectiveFrom ? String(item.effectiveFrom).substring(0, 10) : '',
            effectiveTo: item.effectiveTo ? String(item.effectiveTo).substring(0, 10) : ''
          });
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load policy.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    this.saving = true;
    this.error = null;
    const formValue = this.form.getRawValue();

    const payload = {
      jurisdictionId: String(formValue.jurisdictionId || '').trim(),
      customerType: Number(formValue.customerType),
      productType: Number(formValue.productType),
      tierCode: String(formValue.tierCode || '').trim().toUpperCase(),
      version: Number(formValue.version || 1),
      name: String(formValue.name || '').trim(),
      status: Number(formValue.status),
      requiresManualReview: !!formValue.requiresManualReview,
      requiresSanctionsScreening: !!formValue.requiresSanctionsScreening,
      requiresSourceOfFunds: !!formValue.requiresSourceOfFunds,
      requiresProofOfAddress: !!formValue.requiresProofOfAddress,
      effectiveFrom: formValue.effectiveFrom,
      effectiveTo: formValue.effectiveTo || null
    };

    const request$ = this.isEditMode
      ? this.adminService.updatePolicy(this.id, payload)
      : this.adminService.createPolicy(payload);

    request$
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (res: any) => {
          const savedId = this.id || res?.data?.id || res?.id;

          this.notifyService.showSuccess('Policy saved successfully.', 'Success');

          if (savedId) {
            this.router.navigate(['/admin/compliance/policies', savedId, 'edit']);
          } else {
            this.router.navigate(['/admin/compliance/policies']);
          }
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to save policy.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  onTierChanged(tierId: string): void {
    const selected = this.tiers.find(x => String(x.id) === String(tierId));
    if (!selected) return;

    this.form.patchValue({
      tierCode: selected.tierCode || '',
      customerType: this.getCustomerTypeValue(selected.customerType)
    });

    if (!this.form.value.jurisdictionId) {
      this.form.patchValue({ jurisdictionId: selected.jurisdictionId || '' });
    }

    if (!this.form.value.name) {
      this.form.patchValue({ name: selected.name ? `${selected.name} Policy` : '' });
    }
  }

  private getCustomerTypeValue(customerType: any): number {
    if (typeof customerType === 'number') return customerType;

    const match = this.customerTypes.find(x =>
      String(x.label).toLowerCase() === String(customerType || '').toLowerCase() ||
      String(x.value) === String(customerType || '')
    );

    return match?.value ?? 1;
  }

  private findTierIdForPolicy(policy: any): string {
    const policyTierCode = String(policy?.tierCode || '').trim().toUpperCase();
    const policyCustomerType = this.getCustomerTypeValue(policy?.customerType);

    const match = this.tiers.find(x =>
      String(x.jurisdictionId || '') === String(policy?.jurisdictionId || '') &&
      String(x.tierCode || '').trim().toUpperCase() === policyTierCode &&
      this.getCustomerTypeValue(x.customerType) === policyCustomerType
    );

    return match?.id || '';
  }

  openFields(): void {
    if (!this.id) return;
    this.router.navigate(['/admin/compliance/policies', this.id, 'fields']);
  }

  openDocuments(): void {
    if (!this.id) return;
    this.router.navigate(['/admin/compliance/policies', this.id, 'documents']);
  }

  openRules(): void {
    if (!this.id) return;
    this.router.navigate(['/admin/compliance/policies', this.id, 'rules']);
  }

  cancel(): void {
    this.router.navigate(['/admin/compliance/policies']);
  }

  get availableTiers(): any[] {
    const jurisdictionId = this.form.value.jurisdictionId;
    if (!jurisdictionId) return this.tiers;
    return this.tiers.filter(x => x.jurisdictionId === jurisdictionId);
  }

  get jurisdictionId() { return this.form.get('jurisdictionId'); }
  get tierId() { return this.form.get('tierId'); }
  get tierCode() { return this.form.get('tierCode'); }
  get version() { return this.form.get('version'); }
  get name() { return this.form.get('name'); }
  get effectiveFrom() { return this.form.get('effectiveFrom'); }
}
